import { ProSubscriptionType } from "@lifeat/shared/stripe/StripeSubscriptionTypes";
import { AmplitudeEvent } from "@analytics/Amplitude/AmplitudeEvents";
import { UpgradeCtaContextType } from "@contexts/CallToActionContext";

enum ProSubEvent {
    VIEW_LEARN_MORE_LINK = "view_learn_more_link_pro_upgrade",
    DISPLAY_UPGRADE_CTA = "cta_upgrade",
    ENTER_UPGRADE_FLOW = "enter_upgrade_flow",
    CANCEL_UPGRADE_FLOW = "cancel_upgrade_flow",
    COMPLETE_UPGRADE_FLOW = "complete_upgrade_flow",
    SEND_UPGRADE_DATA_TO_GTAG = "send_upgrade_data_to_gtag",
    VIEW_DISCOUNT_CODE_IN_MODAL_ON_CLOSE = "view_discount_code_in_modal_on_close_attempt",

    SUBMIT_UPGRADE_REASON = "submit_upgrade_reason",
    SUBMIT_CANCEL_REASON = "submit_cancel_reason",
    SUBMIT_CANCEL_FEEDBACK = "submit_cancel_feedback",
}

export enum UpgradeCTASource {
    FOCUS_TIME_METRIC = "focus_time_metric",
    UPGRADE_BUTTON = "upgrade_button",
    WIDGETS_GENERAL = "widgets_general",
    NOTES_WIDGET = "notes_widget",
    SOUNDBOARD = "soundboard",
    POMODORO_SOUND = "pomodoro_sound",
    CALENDAR_SELECTOR = "calendar_selector",
    CALENDAR_AUTH_BUTTON = "calendar_auth_button",
    SPACES = "spaces",
    ROOMS = "rooms",
    DISPLAY_HOST_INFO = "display_host_info",
    /** @deprecated */
    SPACE_SHUFFLES = "space_shuffles",
    FILTER_TAGS = "filter_tags",
    PLANNER = "planner",
    PROJECTS = "projects",
    TASK_TIMER = "task_timer",
}

interface ProSubEventDetails {
    /**
     * from Firebase.User.metadata.creationTime
     */
    accountCreationDate?: string;
}

export interface AdditionalSourceData {
    spaceCategory?: string;
    spaceName?: string;
    triggeredAutomatically?: boolean;
}

interface CTASourceDetails {
    /**
     * Source of event
     */
    source: UpgradeCTASource;
    /**
     * Additional source data
     */
    additionalSourceData?: AdditionalSourceData;
}

export class DisplayUpgradeCTAEvent extends AmplitudeEvent {
    constructor(eventDetails: ProSubEventDetails & CTASourceDetails) {
        super(ProSubEvent.DISPLAY_UPGRADE_CTA);
        this.props = eventDetails;
    }
}

export class EnterProSubUpgradeFlowEvent extends AmplitudeEvent {
    constructor(
        eventDetails: ProSubEventDetails & {
            subscriptionPeriod: ProSubscriptionType;
            eventSource: UpgradeCtaContextType;
            additionalSourceData?: AdditionalSourceData;
        },
    ) {
        super(ProSubEvent.ENTER_UPGRADE_FLOW);
        this.props = eventDetails;
    }
}

export class CancelProSubUpgradeFlowEvent extends AmplitudeEvent {
    constructor() {
        super(ProSubEvent.CANCEL_UPGRADE_FLOW);
    }
}

export class CompleteProSubUpgradeFlowEvent extends AmplitudeEvent {
    constructor(eventDetails: ProSubEventDetails) {
        super(ProSubEvent.COMPLETE_UPGRADE_FLOW);
        this.props = eventDetails;
    }
}

export class SendUpgradeDataToGtagEvent extends AmplitudeEvent {
    constructor(amount: number, tier: ProSubscriptionType, checkoutSessionId: string) {
        super(ProSubEvent.SEND_UPGRADE_DATA_TO_GTAG);
        this.props = { amount, tier, checkoutSessionId, hasError: !tier || amount <= 0 };
    }
}

export class ViewLearnMoreAboutProLink extends AmplitudeEvent {
    constructor(eventDetails: ProSubEventDetails) {
        super(ProSubEvent.VIEW_LEARN_MORE_LINK);
        this.props = eventDetails;
    }
}

export class ViewDiscountCodeInModalOnCloseEvent extends AmplitudeEvent {
    constructor(eventDetails: ProSubEventDetails) {
        super(ProSubEvent.VIEW_DISCOUNT_CODE_IN_MODAL_ON_CLOSE);
        this.props = eventDetails;
    }
}

export class SubmitUpgradeReasonEvent extends AmplitudeEvent {
    constructor(upgradeReason: string) {
        super(ProSubEvent.SUBMIT_UPGRADE_REASON);
        this.props = { upgradeReason: upgradeReason };
    }
}

export class SubmitCancelReasonEvent extends AmplitudeEvent {
    constructor(cancelReason: string) {
        super(ProSubEvent.SUBMIT_CANCEL_REASON);
        this.props = { cancelReason: cancelReason };
    }
}

export class SubmitCancelFeedbackEvent extends AmplitudeEvent {
    constructor(cancelFeedback: string) {
        super(ProSubEvent.SUBMIT_CANCEL_FEEDBACK);
        this.props = { cancelFeedback: cancelFeedback };
    }
}
